import React, { type MutableRefObject, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import tinycolor from 'tinycolor2';

import Button from '@atlaskit/button/default/button';
import { Label } from '@atlaskit/form/label/default';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select/default';
import type { OptionType, ValueType } from '@atlaskit/select/types';

import { convertLegacyColor, extractToken, getSanitizedColor } from '../../../common/utils';

import { HueAndSaturation } from './hue-and-saturation';
import { ColorInput, type ColorInputProps } from './input';
import { messages } from './messages';

const containerStyles = xcss({
	flexDirection: 'column',
	gap: 'space.100',
	width: '292px',
});

const addButtonContainerStyles = xcss({
	alignItems: 'center',
	paddingRight: 'space.100',
});

interface ColorCodeOption extends OptionType {
	label: string;
	value: string;
}

export type CustomPaletteProps = Pick<
	ColorInputProps,
	'builtInColorMap' | 'value' | 'onChange' | 'analyticsSource'
> & {
	customColorRef: MutableRefObject<string | undefined>;
	onSelectColor?: (color: string) => void;
};

const COLOR_CODE_OPTIONS = [
	{ label: 'Hex', value: '#' },
	{ label: 'RGB', value: 'rgb' },
	{ label: 'HSL', value: 'hsl' },
];

export const CustomPalette = ({
	analyticsSource,
	builtInColorMap,
	customColorRef,
	onChange,
	onSelectColor,
	value,
}: CustomPaletteProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [colorToBeAdded, setColorToBeAdded] = useState<string>(value);

	useEffect(() => {
		setColorToBeAdded(value);
	}, [value]);

	useEffect(() => {
		onSelectColor?.(colorToBeAdded);
	}, [colorToBeAdded, onSelectColor]);

	const convertedValue = convertLegacyColor(value);
	const convertedColorToBeAdded = convertLegacyColor(colorToBeAdded);
	const isColorToBeAddedValid = convertedColorToBeAdded.isValid();
	const colorForHueAndSaturation = isColorToBeAddedValid ? convertedColorToBeAdded : convertedValue;

	//
	// Add button
	//

	/* isColorStaged: either the color is already staged, saved, or of default Atlassian theme
	 * isColorInCurated: custom color is already elevated as one of eight default tiles in Curated palette
	 * isTokenizedColorInCurated: custom color is already elevated as first of nine default tiles in Curated palette. The first of nine is a placeholder for custom color.
	 */

	const isColorStaged = colorToBeAdded === value;
	const isColorInCurated = builtInColorMap.knownTokens.includes(extractToken(colorToBeAdded));
	const isTokenizedColorInCurated =
		extractToken(customColorRef.current) === extractToken(colorToBeAdded);

	const isAddButtonDisabled =
		!isColorToBeAddedValid || isColorStaged || isColorInCurated || isTokenizedColorInCurated;

	const onAddClick = () => {
		if (isColorToBeAddedValid) {
			const sanitizedColor = getSanitizedColor(builtInColorMap, colorToBeAdded);
			onChange(sanitizedColor);
		}
	};

	const getColorCode = (color: string) => {
		if (color.startsWith('rgb')) {
			return COLOR_CODE_OPTIONS[1];
		}
		if (color.startsWith('hsl')) {
			return COLOR_CODE_OPTIONS[2];
		}
		return COLOR_CODE_OPTIONS[0];
	};

	const [selectedColorCode, setSelectedColorCode] = useState<ValueType<ColorCodeOption>>(
		getColorCode(value),
	);

	useEffect(() => {
		setSelectedColorCode(getColorCode(colorToBeAdded));
	}, [colorToBeAdded]);

	const onHueAndSaturationChange = useCallback(
		(color: string) => {
			if (selectedColorCode?.value === 'rgb') {
				setColorToBeAdded(tinycolor(color).toRgbString());
			} else if (selectedColorCode?.value === 'hsl') {
				setColorToBeAdded(tinycolor(color).toHslString());
			} else {
				setColorToBeAdded(color);
			}
		},
		[selectedColorCode],
	);

	return (
		<Flex xcss={containerStyles}>
			<HueAndSaturation
				analyticsSource={analyticsSource}
				onChange={onHueAndSaturationChange}
				value={colorForHueAndSaturation}
			/>

			<Box>
				<Select
					label={formatMessage(messages.colorCodeSelectorLabel)}
					classNamePrefix="AkSelect"
					id="color-code-selector"
					inputId="color-code-selector-input"
					options={COLOR_CODE_OPTIONS}
					value={selectedColorCode}
					onChange={setSelectedColorCode}
				/>
				<Label htmlFor="color-input">{formatMessage(messages.colorInputLabel)}</Label>
				<Stack space="space.100">
					<ColorInput
						id="color-input"
						name="color-input"
						analyticsSource={analyticsSource}
						builtInColorMap={builtInColorMap}
						onChange={(color = '') => setColorToBeAdded(color)}
						value={colorToBeAdded}
					>
						<Flex xcss={addButtonContainerStyles}>
							<Button isDisabled={isAddButtonDisabled} onClick={onAddClick} spacing="compact">
								<FormattedMessage {...messages.addButtonText} />
							</Button>
						</Flex>
					</ColorInput>
				</Stack>
			</Box>
		</Flex>
	);
};
